// ============================================================
// QZ Tray + ESC/POS thermal printing
// ------------------------------------------------------------
// Strategy: try to connect to a local QZ Tray instance
// (http://localhost:8181 ws / 8182 wss). If the user hasn't
// installed/started QZ Tray, connect() rejects and the caller
// falls back to HTML browser print.
// ------------------------------------------------------------
// Setup (on the restaurant PC, one-time):
//   1. Download QZ Tray from https://qz.io/download/
//   2. Install, start it (it runs in the system tray)
//   3. First print will show a security prompt → "Allow" +
//      "Remember this decision" for this site.
// ============================================================

import qz from 'qz-tray';
import { LOGO_BASE64 } from '@/utils/logoData';
import { GOOGLE_REVIEW_QR_BASE64, GOOGLE_REVIEW_URL } from '@/utils/googleReviewQrData';

// ---------- connection ----------
let connecting = null;

export const isQzReady = () => qz.websocket.isActive();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load public certificate from /qz-digital-certificate.pem (served from public/)
const loadCertificate = async () => {
  const res = await fetch('/qz-digital-certificate.pem');
  if (!res.ok) throw new Error('Cert not found');
  return res.text();
};

// Ask backend to sign the request data with the matching private key
const signRequest = async (toSign) => {
  const res = await fetch(`${BACKEND_URL}/api/qz/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request: toSign }),
  });
  if (!res.ok) throw new Error('Signing failed');
  const { signature } = await res.json();
  return signature;
};

export const connectQz = async () => {
  if (qz.websocket.isActive()) return true;
  if (connecting) return connecting;

  // Signed mode: cert comes from our server, each print call is signed
  // server-side with the matching private key. This unlocks the
  // "Remember this decision" checkbox in QZ Tray.
  qz.security.setCertificatePromise(function (resolve, reject) {
    loadCertificate().then(resolve).catch(reject);
  });
  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise(function (toSign) {
    return function (resolve, reject) {
      signRequest(toSign).then(resolve).catch(reject);
    };
  });

  connecting = qz.websocket
    .connect({ retries: 0, delay: 1 })
    .then(() => true)
    .catch((err) => { connecting = null; throw err; });

  return connecting;
};

export const getPrinters = async () => {
  await connectQz();
  return qz.printers.find();
};

export const getDefaultPrinter = async () => {
  await connectQz();
  return qz.printers.getDefault();
};

// ---------- ESC/POS command builders ----------
// Raw bytes as string (QZ sends them as-is when type:'raw' format:'command')
const ESC = '\x1B';
const GS = '\x1D';
const LF = '\x0A';

const CMD = {
  INIT: ESC + '@',                     // Initialize printer
  ALIGN_LEFT: ESC + 'a' + '\x00',
  ALIGN_CENTER: ESC + 'a' + '\x01',
  ALIGN_RIGHT: ESC + 'a' + '\x02',
  BOLD_ON: ESC + 'E' + '\x01',
  BOLD_OFF: ESC + 'E' + '\x00',
  DOUBLE_ON: GS + '!' + '\x11',        // double W + H (only used for TOTAL)
  DOUBLE_OFF: GS + '!' + '\x00',
  FEED: (n) => ESC + 'd' + String.fromCharCode(n),
  CUT_FULL: GS + 'V' + '\x00',         // full cut
  CUT_PARTIAL_FEED: GS + 'V' + 'B' + '\x00', // partial cut w/ feed (GS V 66 0)
  CODEPAGE_CP858: ESC + 't' + '\x13',  // 19 = CP858 (Euro + Spanish accents)
};

// 80mm printer line width in characters (font A ≈ 42, font B ≈ 56)
const COLS_80 = 42;
const COLS_58 = 32;

const pad = (s, n) => (s + ' '.repeat(n)).slice(0, n);
const padL = (s, n) => (' '.repeat(n) + s).slice(-n);

const twoCol = (left, right, cols) => {
  const l = String(left), r = String(right);
  if (l.length + 1 + r.length <= cols) return pad(l, cols - r.length) + r;
  return l + LF + padL(r, cols);
};

const wrap = (text, cols) => {
  const words = String(text).split(/\s+/);
  const out = [];
  let cur = '';
  for (const w of words) {
    if ((cur + (cur ? ' ' : '') + w).length > cols) {
      if (cur) out.push(cur);
      let rest = w;
      while (rest.length > cols) { out.push(rest.slice(0, cols)); rest = rest.slice(cols); }
      cur = rest;
    } else {
      cur = cur ? cur + ' ' + w : w;
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
};

const hr = (cols, ch = '-') => ch.repeat(cols) + LF;
const money = (n) => `${Number(n || 0).toFixed(2)} EUR`;

// ---------- build order ticket commands ----------
const buildOrderCommands = (order, cols, fiscalContext = null) => {
  const orderTypeText = order.order_type === 'pickup' ? 'RECOGIDA' : 'ENTREGA';
  const estTime = order.estimated_time || (order.order_type === 'pickup' ? '20 min' : '50 min');
  const subtotal = order.subtotal || order.total;
  const deliveryFee = order.delivery_fee || 0;
  const createdAt = new Date(order.created_at);
  const shortId = order.id.substring(0, 8).toUpperCase();
  const fiscal = fiscalContext?.fiscal || null;
  const ticket = fiscalContext?.ticket || null;
  const qrBase64 = fiscalContext?.qrBase64 || null;

  const cmds = [];
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: CMD.INIT + CMD.CODEPAGE_CP858 });

  // Logo (QZ converts base64 PNG to raster automatically)
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: CMD.ALIGN_CENTER });
  cmds.push({
    type: 'raw',
    format: 'image',
    flavor: 'base64',
    data: LOGO_BASE64.replace(/^data:image\/[a-z]+;base64,/, ''),
    options: { language: 'escpos', dotDensity: 'double' },
  });
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: LF });

  // Address (centered) + fiscal header (NIF + razón social) when configured
  const addrFooter = fiscal && fiscal.nif
    ? wrap(fiscal.razon_social || '', cols).join(LF) + LF +
      'NIF: ' + (fiscal.nif || '') + LF +
      wrap(fiscal.direccion || 'Calle Real 109', cols).join(LF) + LF +
      ((fiscal.codigo_postal || fiscal.municipio)
        ? wrap([fiscal.codigo_postal, fiscal.municipio].filter(Boolean).join(' ') + (fiscal.provincia ? ' (' + fiscal.provincia + ')' : ''), cols).join(LF) + LF
        : '') +
      '+34 602 704 745' + LF
    : 'Calle Real 109' + LF +
      'San Fernando, Cadiz' + LF +
      '+34 602 704 745' + LF;

  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data: addrFooter + CMD.ALIGN_LEFT + hr(cols, '='),
  });

  // Fiscal-mode banner (FACTURA SIMPLIFICADA / VERI*FACTU) when a ticket
  // exists. AEAT requires this label visible on the receipt.
  if (ticket) {
    const isVf = ticket.verifactu_mode === 'verifactu';
    const banner = isVf ? 'VERI*FACTU' : 'FACTURA SIMPLIFICADA';
    cmds.push({
      type: 'raw', format: 'command', flavor: 'plain',
      data:
        CMD.ALIGN_CENTER + CMD.BOLD_ON + banner + CMD.BOLD_OFF + LF +
        ticket.full_number + LF +
        CMD.ALIGN_LEFT + hr(cols),
    });
  }

  // Order info — TIPO line is bold so the kitchen instantly sees
  // RECOGIDA vs ENTREGA when the ticket comes off the printer.
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      twoCol('PEDIDO:', '#' + shortId, cols) + LF +
      twoCol('FECHA:', createdAt.toLocaleDateString('es-ES'), cols) + LF +
      twoCol('HORA:', createdAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), cols) + LF +
      CMD.BOLD_ON + twoCol('TIPO:', `${orderTypeText} (${estTime})`, cols) + CMD.BOLD_OFF + LF +
      hr(cols),
  });

  // Customer — "Notas:" line bolded if present so the cook doesn't miss it.
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      CMD.BOLD_ON + 'CLIENTE' + CMD.BOLD_OFF + LF + hr(cols) +
      wrap('Nombre: ' + (order.customer_name || '-'), cols).join(LF) + LF +
      wrap('Tel:    ' + (order.customer_phone || '-'), cols).join(LF) + LF +
      (order.order_type === 'delivery' && order.customer_address
        ? wrap('Dir:    ' + order.customer_address, cols).join(LF) + LF : '') +
      (order.notes
        ? CMD.BOLD_ON + wrap('Notas:  ' + order.notes, cols).join(LF) + CMD.BOLD_OFF + LF
        : '') +
      hr(cols),
  });

  // Items
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data: CMD.BOLD_ON + 'ARTICULOS' + CMD.BOLD_OFF + LF + hr(cols),
  });

  for (const i of order.items) {
    // Menu Infantil items print as "MENU INFANTIL [POLLO CON SALSA]" so
    // the kitchen knows it's a kids portion with the standard sides.
    // Some kids items already carry "Menu Infantil" as part of their
    // display name (multi-line title on the public menu) — strip that
    // duplication before computing the receipt label.
    const isKids = (i.category || '').toLowerCase() === 'menu infantil';
    const cleanName = (i.name || '').replace(/^menu infantil\s*/i, '').trim();
    const rawName = cleanName.toUpperCase();
    const name = isKids ? `MENU INFANTIL [${rawName}]` : rawName;
    const qty = String(i.quantity);
    const prefix = qty + ' ';
    const lineTotal = (i.price * i.quantity).toFixed(2);
    const firstAvail = cols - prefix.length - 1 - lineTotal.length;
    const indent = ' '.repeat(prefix.length);
    const words = name.split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = '', limit = firstAvail;
    for (const w of words) {
      const cand = cur ? cur + ' ' + w : w;
      if (cand.length <= limit) { cur = cand; }
      else {
        if (cur) lines.push(cur);
        let rest = w; const nextLimit = cols - indent.length;
        while (rest.length > nextLimit) { lines.push(rest.slice(0, nextLimit)); rest = rest.slice(nextLimit); }
        cur = rest; limit = nextLimit;
      }
    }
    if (cur) lines.push(cur);
    const firstLineText = prefix + (lines[0] || '');
    let firstLine = pad(firstLineText, cols - lineTotal.length) + lineTotal;
    let contLines = lines.slice(1).map((l) => indent + l);
    // Bold the parenthesised variant in the name, e.g.
    //   "TIKKA MASALA (POLLO)" → "TIKKA MASALA " + bold("(POLLO)")
    // Applied AFTER padding so the price column stays aligned (the
    // escape codes don't take printed space).
    const boldParens = (s) => s.replace(/\(([^)]+)\)/g, CMD.BOLD_ON + '($1)' + CMD.BOLD_OFF);
    firstLine = boldParens(firstLine);
    contLines = contLines.map(boldParens);
    // Variant choices (spiciness / side dish) printed bold so the cook
    // immediately spots them under the item name.
    const extras = [];
    if (i.spiciness) extras.push(CMD.BOLD_ON + indent + '(' + i.spiciness + ')' + CMD.BOLD_OFF);
    if (i.side_dish) extras.push(CMD.BOLD_ON + indent + '(' + i.side_dish + ')' + CMD.BOLD_OFF);
    if (i.notes) { for (const l of wrap(i.notes, cols - indent.length)) extras.push(indent + l); }
    const block = [firstLine, ...contLines, ...extras, ''].join(LF) + LF;
    cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: block });
  }

  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: hr(cols) });

  // Totals
  const discAmt = Number(order.discount_amount || 0);
  const discPct = Number(order.discount_percentage || 0);
  const discSrc = order.discount_source || (order.discount_code ? 'code' : 'none');
  let discLabel = '';
  if (discAmt > 0) {
    if (discSrc === 'pickup') discLabel = `Recogida -${discPct.toFixed(0)}%:`;
    else discLabel = `Cod. ${order.discount_code || ''} -${discPct.toFixed(0)}%:`;
  }
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      twoCol('Subtotal:', money(subtotal), cols) + LF +
      (discAmt > 0 ? twoCol(discLabel, '-' + money(discAmt), cols) + LF : '') +
      (deliveryFee > 0 ? twoCol('Envio:', money(deliveryFee), cols) + LF : '') +
      hr(cols, '=') +
      CMD.BOLD_ON + CMD.DOUBLE_ON +
      twoCol('TOTAL:', money(order.total), cols / 2 | 0) + LF +
      CMD.DOUBLE_OFF + CMD.BOLD_OFF +
      hr(cols, '='),
  });

  // IVA breakdown (mandatory on simplified invoices)
  if (ticket && ticket.vat) {
    const v = ticket.vat;
    cmds.push({
      type: 'raw', format: 'command', flavor: 'plain',
      data:
        twoCol(`Base imponible (IVA ${Number(v.tipo).toFixed(0)}%):`, money(v.base), cols) + LF +
        twoCol('Cuota IVA:', money(v.cuota), cols) + LF +
        hr(cols),
    });
  }

  // Payment method
  let payment = 'Tarjeta Online (Pagado)';
  if (order.payment_method === 'whatsapp') {
    if (order.payment_mode === 'cash') {
      payment = order.cash_amount > 0
        ? 'Efectivo' + LF +
          twoCol('  Paga con:', money(order.cash_amount), cols) + LF +
          twoCol('  Cambio:', money(order.cash_amount - order.total), cols)
        : 'Efectivo (Importe exacto)';
    } else {
      payment = 'Tarjeta al repartidor/recogida';
    }
  }
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: payment + LF + hr(cols) });

  // VeriFactu / AEAT block: QR code + huella + mode label.
  // Required by RD 1007/2023 on every ticket once tickets are being issued.
  if (ticket) {
    const isVf = ticket.verifactu_mode === 'verifactu';
    cmds.push({
      type: 'raw', format: 'command', flavor: 'plain',
      data:
        CMD.ALIGN_CENTER + CMD.BOLD_ON + (isVf ? 'VERI*FACTU' : 'FACTURA SIMPLIFICADA') + CMD.BOLD_OFF + LF +
        'Verificable en la sede de la AEAT' + LF,
    });
    if (qrBase64) {
      cmds.push({
        type: 'raw', format: 'image', flavor: 'base64',
        data: qrBase64,
        options: { language: 'escpos', dotDensity: 'double' },
      });
    }
    cmds.push({
      type: 'raw', format: 'command', flavor: 'plain',
      data:
        'Huella: ' + (ticket.current_hash || '').substring(0, 16) + '...' + LF +
        CMD.ALIGN_LEFT + hr(cols),
    });
  }

  // Footer with Google review QR code
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      CMD.ALIGN_CENTER +
      CMD.BOLD_ON + 'Gracias por su pedido!' + CMD.BOLD_OFF + LF +
      'www.tajmahalsanfernando.es' + LF + LF +
      '--- Deja tu resena en Google ---' + LF,
  });
  cmds.push({
    type: 'raw', format: 'image', flavor: 'base64',
    data: GOOGLE_REVIEW_QR_BASE64.replace(/^data:image\/[a-z]+;base64,/, ''),
    options: { language: 'escpos', dotDensity: 'double' },
  });
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      GOOGLE_REVIEW_URL + LF +
      CMD.ALIGN_LEFT +
      CMD.FEED(5) +
      CMD.CUT_PARTIAL_FEED,
  });

  return cmds;
};

// ---------- build daily report commands ----------
const buildDailyReportCommands = (data, cols) => {
  const dateFormatted = new Date(data.date).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const cmds = [];
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: CMD.INIT + CMD.CODEPAGE_CP858 });

  // Logo
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: CMD.ALIGN_CENTER });
  cmds.push({
    type: 'raw', format: 'image', flavor: 'base64',
    data: LOGO_BASE64.replace(/^data:image\/[a-z]+;base64,/, ''),
    options: { language: 'escpos', dotDensity: 'double' },
  });
  cmds.push({ type: 'raw', format: 'command', flavor: 'plain', data: LF });

  // Header
  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      CMD.BOLD_ON + 'INFORME DE VENTAS DIARIO' + CMD.BOLD_OFF + LF +
      dateFormatted + LF +
      CMD.ALIGN_LEFT + hr(cols, '='),
  });

  const section = (title, lines) =>
    LF + CMD.BOLD_ON + title + CMD.BOLD_OFF + LF + hr(cols) + lines.join('') + LF;

  cmds.push({
    type: 'raw', format: 'command', flavor: 'plain',
    data:
      section('RESUMEN', [
        twoCol('Total Pedidos:', String(data.total_orders), cols) + LF,
        twoCol('Cancelados:', String(data.cancelled_orders), cols) + LF,
        hr(cols),
        CMD.BOLD_ON + twoCol('INGRESOS:', money(data.total_revenue), cols) + CMD.BOLD_OFF + LF,
      ]) +
      section('POR CANAL', [
        twoCol(`Web (${data.online_count}):`, money(data.online_revenue), cols) + LF,
        twoCol(`WhatsApp (${data.whatsapp_count}):`, money(data.whatsapp_revenue), cols) + LF,
      ]) +
      section('POR METODO DE PAGO', [
        twoCol(`Tarjeta (${data.card_count}):`, money(data.card_revenue), cols) + LF,
        twoCol(`Efectivo (${data.cash_count}):`, money(data.cash_revenue), cols) + LF,
      ]) +
      section('POR TIPO', [
        twoCol(`Entrega (${data.delivery_count}):`, money(data.delivery_revenue), cols) + LF,
        twoCol(`Recogida (${data.pickup_count}):`, money(data.pickup_revenue), cols) + LF,
      ]) +
      hr(cols, '=') +
      CMD.ALIGN_CENTER + 'Generado: ' + new Date().toLocaleString('es-ES') + LF +
      'www.tajmahalsanfernando.es' + LF +
      CMD.ALIGN_LEFT +
      CMD.FEED(5) +
      CMD.CUT_PARTIAL_FEED,
  });

  return cmds;
};

// ---------- public API ----------
// opts.printer - override printer name; otherwise use system default
// opts.width - '80mm' (default) | '58mm'
// opts.copies - number of copies to print (default 1)
export const printOrderViaQz = async (order, opts = {}) => {
  await connectQz();
  const printerName = opts.printer || (await qz.printers.getDefault());
  if (!printerName) throw new Error('No hay impresora predeterminada en el sistema');
  const cols = opts.width === '58mm' ? COLS_58 : COLS_80;
  const copies = Math.max(1, parseInt(opts.copies || 1, 10));
  const config = qz.configs.create(printerName, { encoding: 'CP858', copies });
  const data = buildOrderCommands(order, cols, opts.fiscalContext);
  return qz.print(config, data);
};

export const printDailyReportViaQz = async (report, opts = {}) => {
  await connectQz();
  const printerName = opts.printer || (await qz.printers.getDefault());
  if (!printerName) throw new Error('No hay impresora predeterminada en el sistema');
  const cols = opts.width === '58mm' ? COLS_58 : COLS_80;
  const copies = Math.max(1, parseInt(opts.copies || 1, 10));
  const config = qz.configs.create(printerName, { encoding: 'CP858', copies });
  const data = buildDailyReportCommands(report, cols);
  return qz.print(config, data);
};

// Best-effort silent probe (doesn't throw, returns bool)
export const probeQz = async () => {
  try { await connectQz(); return true; } catch { return false; }
};
